/*
 * The sign-in, LeadCue's one Moment screen (docs/2026-10-19-sign-in.md, DESIGN.md "Typography" and
 * "The Brand Stroke"). An example reply drawn with the inbox's own message card and intent tag, a
 * greeting for the visitor's hour in Ronzino with the brand stroke written under its last word, and the
 * form. The arrival plays once, in CSS: the reply settles, the stroke is written, the reading lands.
 */
import { useContext, useEffect, useState } from "react";
import { authClient } from "../auth-client.ts";
import { LabelDot } from "../inbox-v2/label-key.tsx";
import { failureMessage, greetingFor, type SignInFailure, signInOutcome } from "./sign-in-copy.ts";
import { DarkThemeContext } from "./theme.ts";

/** The brand stroke's own path (`--lcr-brand-stroke` in styles.css), inline so it can be written in. */
const BRAND_STROKE =
  "M4 9.9C6.3 9.6 13.5 8.7 18 8.3C22.5 8 26.5 8 31 8C35.5 8 40.5 8.3 45 8.5C49.5 8.8 53.5 9.6 58 9.5C62.5 9.4 67.5 8.8 72 8.2C76.5 7.6 80.5 6.2 85 6C89.5 5.8 96.7 6.7 99 6.8";

/** The hour of the visitor's own clock, read again when they come back to the tab. */
function useLocalHour(): number {
  const [hour, setHour] = useState(() => new Date().getHours());
  useEffect(() => {
    const refresh = () => setHour(new Date().getHours());
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, []);
  return hour;
}

/** One sign-in attempt, as an outcome rather than an exception. */
async function signInWithEmail(email: string, password: string) {
  try {
    let status = 0;
    let retryAfter: string | null = null;
    await authClient.signIn.email(
      { email, password },
      {
        onResponse: ({ response }) => {
          status = response.status;
          retryAfter = response.headers.get("retry-after");
        },
      },
    );
    return signInOutcome(status, retryAfter);
  } catch {
    return signInOutcome(0, null);
  }
}

/** A heading with the brand stroke under its last word. */
function Underlined(props: { text: string }) {
  const cut = props.text.lastIndexOf(" ");
  return (
    <>
      {props.text.slice(0, cut + 1)}
      <span className="lcr-signin-stroke">
        {props.text.slice(cut + 1)}
        <svg viewBox="0 0 104 17" preserveAspectRatio="none" aria-hidden="true" focusable="false">
          <path pathLength={1} d={BRAND_STROKE} />
        </svg>
      </span>
    </>
  );
}

/** A made-up reply, drawn as the inbox draws a received message, and labelled as an example. */
function ExampleReply() {
  return (
    <figure className="lcr-signin-example" aria-hidden="true">
      <article className="lcr-message lcr-i2-message lcr-received">
        <div className="lcr-mail-head">
          <strong className="lcr-mail-from">Maya Lind</strong>
          <span className="lcr-i2-intent">
            <LabelDot code="question" />
            Question
          </span>
          <time>09:14</time>
        </div>
        <dl className="lcr-email-fields">
          <dt>Subject</dt>
          <dd>Re: Carrier invoice audits</dd>
        </dl>
        <div className="lcr-mail-frame">
          <p className="lcr-signin-example-text">
            Thanks for the note. Before Thursday, could you send pricing for 40 seats, and say
            whether onboarding is included?
          </p>
        </div>
      </article>
      <figcaption className="lcr-signin-caption">Example reply · not from your inbox</figcaption>
    </figure>
  );
}

export function SignIn(props: {
  onSuccess: () => Promise<void>;
  /** This page saw a session that has since ended (a sign-out reloads the page, so it is never that). */
  ended: boolean;
}) {
  const dark = useContext(DarkThemeContext);
  const hour = useLocalHour();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [failure, setFailure] = useState<SignInFailure | null>(null);
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);
  useEffect(() => {
    if (failure?.kind !== "limited") return;
    setWaiting(true);
    const timer = setTimeout(() => setWaiting(false), failure.retryAfterMs);
    return () => clearTimeout(timer);
  }, [failure]);

  const heading = greetingFor(hour);
  const sub = props.ended
    ? "You were signed out. Sign in to carry on where you left off."
    : "Sign in to LeadCue.";
  return (
    <main className="lcr-signin is-arriving">
      <img
        className="lcr-signin-mark"
        src={dark ? "/leadcue-logo-white.svg" : "/leadcue-logo-dark.svg"}
        alt="LeadCue"
      />
      <div className="lcr-signin-col">
        <ExampleReply />
        <h1 className="lcr-signin-greeting">
          <Underlined text={heading} />
        </h1>
        <p className="lcr-signin-sub">{sub}</p>
        <form
          className="lcr-signin-form"
          onSubmit={async (event) => {
            event.preventDefault();
            setBusy(true);
            setFailure(null);
            const outcome = await signInWithEmail(email, password);
            if (outcome.kind === "signed_in") {
              await props.onSuccess();
              return;
            }
            setFailure(outcome);
            setBusy(false);
          }}
        >
          <label className="lcr-signin-field">
            Email
            <input
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          {/* A button inside a label would become what the label names, so this one points by id. */}
          <div className="lcr-signin-field">
            <span className="lcr-signin-row">
              <label htmlFor="lcr-signin-password">Password</label>
              <a className="lcr-linklike" href="/forgot-password">
                Forgot password?
              </a>
            </span>
            <input
              id="lcr-signin-password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              aria-invalid={failure?.kind === "rejected" || undefined}
              aria-describedby={failure ? "lcr-signin-failure" : undefined}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>
          {failure && (
            <p id="lcr-signin-failure" className="lcr-signin-alert" role="alert">
              {failureMessage(failure)}
            </p>
          )}
          {/* Named by its words alone: the keycap is a hint, not part of the name ("Sign in", not "Sign in Enter"). */}
          <button
            className="lcr-primary lcr-signin-submit"
            type="submit"
            disabled={busy || waiting}
            aria-label={busy ? "Signing in…" : "Sign in"}
          >
            {busy ? "Signing in…" : "Sign in"}
            <kbd className="lcr-kbd">Enter</kbd>
          </button>
        </form>
      </div>
      <p className="lcr-signin-foot">Invite only. Ask your admin for access.</p>
    </main>
  );
}
